import { create } from "@bufbuild/protobuf";
import { FieldMaskSchema } from "@bufbuild/protobuf/wkt";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { authServiceClient, shortcutServiceClient, userServiceClient } from "@/connect";
import { buildUserSettingName } from "@/helpers/resource-names";
import {
  User,
  UserSetting,
  UserSetting_GeneralSetting,
  UserSetting_GeneralSettingSchema,
  UserSetting_Key,
  UserSettingSchema,
} from "@/types/proto/api/v1/user_service_pb";

// Query keys factory
export const userKeys = {
  all: ["users"] as const,
  details: () => [...userKeys.all, "detail"] as const,
  detail: (name: string) => [...userKeys.details(), name] as const,
  stats: () => [...userKeys.all, "stats"] as const,
  userStats: (name: string) => [...userKeys.stats(), name] as const,
  currentUser: () => [...userKeys.all, "current"] as const,
  shortcuts: () => [...userKeys.all, "shortcuts"] as const,
  notifications: () => [...userKeys.all, "notifications"] as const,
  byNames: (names: string[]) => [...userKeys.all, "byNames", ...names.sort()] as const,
};

// NOTE: This hook is currently UNUSED in favor of the AuthContext-based
// useCurrentUser hook (src/hooks/useCurrentUser.ts). This is kept for potential
// future migration to React Query for auth state.
export function useCurrentUserQuery() {
  return useQuery({
    queryKey: userKeys.currentUser(),
    queryFn: async () => {
      const { user } = await authServiceClient.getCurrentUser({});
      return user;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes - auth doesn't change often
  });
}

export function useUser(name: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: userKeys.detail(name),
    queryFn: async () => {
      const user = await userServiceClient.getUser({ name });
      return user;
    },
    enabled: options?.enabled ?? true,
    staleTime: 1000 * 60 * 5, // 5 minutes - user profiles don't change often
  });
}

export function useUserStats(username?: string) {
  return useQuery({
    queryKey: username ? userKeys.userStats(username) : userKeys.stats(),
    queryFn: async () => {
      if (!username) {
        throw new Error("Username is required");
      }
      const stats = await userServiceClient.getUserStats({ name: username });
      return stats;
    },
    enabled: !!username,
  });
}

export function useShortcuts() {
  return useQuery({
    queryKey: userKeys.shortcuts(),
    queryFn: async () => {
      const { shortcuts } = await shortcutServiceClient.listShortcuts({});
      return shortcuts;
    },
  });
}

export function useNotifications() {
  const { data: currentUser } = useCurrentUserQuery();

  return useQuery({
    queryKey: userKeys.notifications(),
    queryFn: async () => {
      if (!currentUser?.name) {
        return [];
      }
      const { notifications } = await userServiceClient.listUserNotifications({ parent: currentUser.name });
      return notifications;
    },
    enabled: !!currentUser?.name,
    staleTime: 1000 * 15, // 15 seconds - notifications should update frequently
  });
}

export function useTagCounts(forCurrentUser = false) {
  const { data: currentUser } = useCurrentUserQuery();

  return useQuery({
    // For current user, use userKeys.userStats() to share query with useTickerCounts
    queryKey: forCurrentUser && currentUser?.name ? userKeys.userStats(currentUser.name) : [...userKeys.stats(), "tagCounts", "all"],
    queryFn: async () => {
      if (!forCurrentUser) {
        // NOTE: Previously aggregated from all users using listAllUserStats.
        // Now returns empty - reserved for future Explore page redesign.
        // See: https://github.com/xinghui/valuation/issues/83
        return {};
      }
      if (!currentUser?.name) {
        return {};
      }
      const stats = await userServiceClient.getUserStats({ name: currentUser.name });
      return stats;
    },
    enabled: !forCurrentUser || !!currentUser?.name,
    staleTime: 1000 * 60 * 2, // 2 minutes - tags don't change frequently
    select: (data) => {
      // When forCurrentUser=true, extract tagCount from UserStats
      if (typeof data === "object" && data !== null && "tagCount" in data) {
        return (data as { tagCount?: Record<string, number> }).tagCount || {};
      }
      return data;
    },
  });
}

export function useTickerCounts(forCurrentUser = false) {
  const { data: currentUser } = useCurrentUserQuery();

  return useQuery({
    // For current user, use userKeys.userStats() to share query with useTagCounts
    queryKey: forCurrentUser && currentUser?.name ? userKeys.userStats(currentUser.name) : [...userKeys.stats(), "tickerCounts", "all"],
    queryFn: async () => {
      if (!forCurrentUser) {
        // NOTE: Previously aggregated from all users using listAllUserStats.
        // Now returns empty - reserved for future Explore page redesign.
        // See: https://github.com/xinghui/valuation/issues/83
        return {};
      }
      if (!currentUser?.name) {
        return {};
      }
      const stats = await userServiceClient.getUserStats({ name: currentUser.name });
      return stats;
    },
    enabled: !forCurrentUser || !!currentUser?.name,
    staleTime: 1000 * 60 * 2, // 2 minutes - tickers don't change frequently
    select: (data) => {
      // When forCurrentUser=true, extract tickerCount from UserStats
      if (typeof data === "object" && data !== null && "tickerCount" in data) {
        return (data as { tickerCount?: Record<string, number> }).tickerCount || {};
      }
      return data;
    },
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ user, updateMask }: { user: Partial<User>; updateMask: string[] }) => {
      const updatedUser = await userServiceClient.updateUser({
        user: user as User,
        updateMask: create(FieldMaskSchema, { paths: updateMask }),
      });
      return updatedUser;
    },
    onSuccess: (updatedUser) => {
      queryClient.setQueryData(userKeys.detail(updatedUser.name), updatedUser);
      queryClient.invalidateQueries({ queryKey: userKeys.currentUser() });
    },
  });
}

export function useDeleteUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (name: string) => {
      await userServiceClient.deleteUser({ name });
      return name;
    },
    onSuccess: (name) => {
      queryClient.removeQueries({ queryKey: userKeys.detail(name) });
      queryClient.invalidateQueries({ queryKey: userKeys.all });
    },
  });
}

// Hook to fetch user settings
export function useUserSettings(parent?: string) {
  return useQuery({
    queryKey: [...userKeys.all, "settings", parent],
    queryFn: async () => {
      if (!parent) return { settings: [], shortcuts: [] };
      const [{ settings }, { shortcuts }] = await Promise.all([
        userServiceClient.listUserSettings({ parent }),
        shortcutServiceClient.listShortcuts({ parent }),
      ]);
      return { settings, shortcuts };
    },
    enabled: !!parent,
  });
}

// Hook to update user setting
export function useUpdateUserSetting() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ setting, updateMask }: { setting: UserSetting; updateMask: string[] }) => {
      const updatedSetting = await userServiceClient.updateUserSetting({
        setting,
        updateMask: create(FieldMaskSchema, { paths: updateMask }),
      });
      return updatedSetting;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...userKeys.all, "settings"] });
    },
  });
}

// Hook to list all users
export function useListUsers() {
  return useQuery({
    queryKey: userKeys.all,
    queryFn: async () => {
      const { users } = await userServiceClient.listUsers({});
      return users;
    },
  });
}

// Hook to update user general setting (convenience wrapper)
export function useUpdateUserGeneralSetting(currentUserName?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ generalSetting, updateMask }: { generalSetting: Partial<UserSetting_GeneralSetting>; updateMask: string[] }) => {
      if (!currentUserName) {
        throw new Error("No current user");
      }

      const settingName = buildUserSettingName(currentUserName, UserSetting_Key.GENERAL);

      // Get existing general setting first to preserve other fields
      const existingSetting = await userServiceClient.getUserSetting({ name: settingName });

      const existingGeneral = existingSetting.value?.case === "generalSetting" ? existingSetting.value.value : undefined;

      // Merge with existing values (only update fields provided in generalSetting)
      const mergedGeneral: UserSetting_GeneralSetting = existingGeneral
        ? ({ ...existingGeneral, ...generalSetting } as UserSetting_GeneralSetting)
        : (generalSetting as UserSetting_GeneralSetting);

      // Create GeneralUserSetting message first, then wrap in UserSetting
      const generalSettingMsg = create(UserSetting_GeneralSettingSchema, mergedGeneral);

      const userSetting = create(UserSettingSchema, {
        name: settingName,
        value: {
          case: "generalSetting",
          value: generalSettingMsg,
        },
      });

      const updatedSetting = await userServiceClient.updateUserSetting({
        setting: userSetting,
        updateMask: create(FieldMaskSchema, { paths: updateMask }),
      });
      return updatedSetting;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...userKeys.all, "settings"] });
    },
  });
}

// Hook to fetch multiple users by names (returns Map<name, User>)
export function useUsersByNames(names: string[]) {
  const enabled = names.length > 0;
  const uniqueNames = Array.from(new Set(names));

  return useQuery({
    queryKey: userKeys.byNames(uniqueNames),
    queryFn: async () => {
      const users = await Promise.all(
        uniqueNames.map(async (name) => {
          try {
            const user = await userServiceClient.getUser({ name });
            return { name, user };
          } catch {
            return { name, user: undefined };
          }
        }),
      );

      const userMap = new Map<string, User | undefined>();
      for (const { name, user } of users) {
        userMap.set(name, user);
      }
      return userMap;
    },
    enabled,
    staleTime: 1000 * 60 * 5, // 5 minutes - user profiles don't change often
  });
}
